<template>
  <div class="home">
    <!-- Hero Section -->
    <div class="hero">
      <div class="hero-content">
        <h1 class="hero-title">
          {{ $t('views.new_login.hero_title_1') }}
          <span class="highlight">{{ $t('views.new_login.hero_title_2') }}</span>
        </h1>
        <p class="hero-subtitle">
          {{ $t('views.new_login.hero_subtitle') }}
        </p>
      </div>

      <!-- Statistics overlapping Hero -->
      <div class="stats-container">
        <div class="stat-card">
          <span class="stat-label">{{ $tc('views.new_login.offers_title', offersCount) }}</span>
          <div class="stat-value-group">
            <span class="stat-value">{{ offersCount }}</span>
            <span v-if="offersTrend > 0" class="stat-trend trend-up">↗ {{ offersTrend }}%</span>
          </div>
          <span class="stat-meta">{{ $t('views.new_login.offers_meta') }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{ $tc('views.new_login.companies_title', companiesCount) }}</span>
          <div class="stat-value-group">
            <span class="stat-value">{{ companiesCount }}</span>
            <span v-if="companiesTrend > 0" class="stat-trend trend-up">↗ {{ companiesTrend }}%</span>
          </div>
          <span class="stat-meta">{{ $t('views.new_login.companies_meta') }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{
            $tc('views.new_login.students_and_graduates_title', studentsAndGraduatesCount)
          }}</span>
          <div class="stat-value-group">
            <span class="stat-value">{{ studentsAndGraduatesCount }}</span>
            <span v-if="studentsTrend > 0" class="stat-trend trend-up">↗ {{ studentsTrend }}%</span>
          </div>
          <span class="stat-meta">{{ $t('views.new_login.students_meta') }}</span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content container-fluid">
      <div class="row main-row">
        <!-- Job Offers Section -->
        <div class="col-12 col-lg-8 main-grid-col">
          <div class="section-header">
            <h2 class="section-title">{{ $t('views.new_login.offers_grid_title') }}</h2>
            <router-link :to="{ name: 'login_user_path' }" class="view-all">{{
              $t('views.new_login.view_all')
            }}</router-link>
          </div>

          <div class="filter-group">
            <button class="filter-btn" :class="{ active: selectedOfferType === 'all' }" @click="selectOfferType('all')">
              {{ $t('views.new_login.all') }}
            </button>
            <button class="filter-btn" :class="{ active: selectedOfferType === 'job' }" @click="selectOfferType('job')">
              {{ $t('views.new_login.job') }}
            </button>
            <button
              class="filter-btn"
              :class="{ active: selectedOfferType === 'practice' }"
              @click="selectOfferType('practice')"
            >
              {{ $t('views.new_login.practice') }}
            </button>
            <button
              class="filter-btn"
              :class="{ active: selectedOfferType === 'training' }"
              @click="selectOfferType('training')"
            >
              {{ $t('views.new_login.training') }}
            </button>
          </div>

          <div v-if="offersLoaded" class="job-grid">
            <router-link :to="{ name: 'login_user_path' }" v-for="offer in offers" :key="offer.id" class="job-card">
              <div class="job-card-header">
                <div class="company-logo-placeholder">
                  <img v-if="offer.company && offer.company.logo" :src="offer.company.logo.url" class="img-fluid" />
                  <div v-else class="logo-text">LOGO</div>
                </div>
                <span class="job-type-tag" :class="`type-${offer.offerType}`">
                  {{ $t(`general.${offer.offerType}`) }}
                </span>
              </div>
              <div class="job-card-body">
                <h3 class="job-title">{{ offer.jobTitle }}</h3>
                <p class="job-company">{{ offer.company ? offer.company.name : '' }}</p>
                <div class="tech-tags">
                  <span
                    v-for="(competence, index) in offer.competences"
                    :key="`competence-${offer.id}-${competence.id}`"
                    class="tech-tag"
                  >
                    {{ competence.name }}
                  </span>
                </div>
              </div>
              <div class="job-card-footer">
                <span class="job-salary">
                  <template v-if="offer.salaryFrom || offer.salaryTo">
                    {{ offer.salaryFrom ? offer.salaryFrom : '' }} - {{ offer.salaryTo ? offer.salaryTo : '' }} PLN
                  </template>
                  <template v-else>
                    {{ $t('views.new_login.not_specified') }}
                  </template>
                </span>
                <span class="job-date">{{ getDuration(offer) }}</span>
              </div>
            </router-link>
          </div>
          <div v-if="offersLoaded && offers.length === 0" class="no-records">
            {{ $t('views.new_login.no_offers') }}
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-12 col-lg-4 sidebar-col">
          <!-- Newsletter -->
          <div class="newsletter-card">
            <h3 class="widget-title">{{ $t('views.new_login.newsletter_title') }}</h3>
            <p class="widget-text">{{ $t('views.new_login.newsletter_text') }}</p>
            <div class="newsletter-form">
              <input
                type="email"
                :placeholder="$t('views.new_login.newsletter_placeholder')"
                class="newsletter-input"
              />
              <router-link :to="{ name: 'login_user_path' }" class="btn-submit">{{
                $t('views.new_login.newsletter_button')
              }}</router-link>
            </div>
          </div>

          <!-- Latest Employers -->
          <h3 class="widget-title-small">{{ $t('views.new_login.latest_employers_title') }}</h3>
          <div v-if="companiesLoaded" class="employer-list">
            <div v-for="company in companies" :key="company.id" class="employer-item">
              <div class="employer-logo-sm">
                <img v-if="company.logo" :src="company.logo.url" class="img-fluid" />
              </div>
              <div class="employer-info">
                <span class="employer-name">{{ company.name }}</span>
                <span class="employer-offers">{{ $tc('views.new_login.new_offers_count', company.offersCount) }}</span>
              </div>
            </div>
          </div>

          <!-- News -->
          <h3 class="widget-title-small">{{ $t('views.new_login.latest_news_title') }}</h3>
          <div v-if="newsLoaded" class="news-list">
            <div v-for="item in news" :key="item.id" class="news-item">
              <span class="news-date">{{ formatDate(item.createdAt, 'DD MMM YYYY').toUpperCase() }}</span>
              <h4 class="news-item-title">{{ item.title }}</h4>
              <span class="news-item-text">{{ item.metaDescription }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <b-modal
      ref="acceptancesModal"
      centered
      hide-header
      v-bind:ok-title="$t('general.save_and_login')"
      ok-variant="primary"
      :no-close-on-esc="true"
      @ok="submitAcceptances"
      @cancel="logout"
      v-bind:cancel-title="$t('general.cancel')"
      cancel-variant="default"
      footer-class="abk-modal-footer"
      content-class="abk-modal-content"
    >
      <h2 class="missing-approvals">{{ $t('views.edit_company_profile.approvals') }}</h2>
      <div class="missing-approvals-text">{{ $t('general.missing_approvals') }}</div>
      <h3 v-if="currentAcceptances.cv && currentAcceptances.cv.length > 0">{{ $t('general.student_graduate') }}</h3>
      <b-row
        class="d-flex justify-content-center mb-3"
        v-for="(acceptance, index) in currentAcceptances.cv"
        :key="`cv-approval-${index}`"
      >
        <pcg-checkbox
          variant="normal"
          class="col-12"
          :required="acceptance.required"
          :show-error="errorExist(`acceptances.${index}.value`)"
          :error-text="getError(`acceptances.${index}.value`)"
          v-model="acceptance.value"
        >
          <span v-html="acceptance.text"></span>
        </pcg-checkbox>
      </b-row>
      <h3 v-if="currentAcceptances.company && currentAcceptances.company.length > 0">
        {{ $t('general.employer_recruiter') }}
      </h3>
      <b-row
        class="d-flex justify-content-center mb-3"
        v-for="(acceptance, index) in currentAcceptances.company"
        :key="`company-approval-${index}`"
      >
        <pcg-checkbox
          variant="normal"
          class="col-12"
          :required="acceptance.required"
          :show-error="errorExist(`acceptances.${index}.value`)"
          :error-text="getError(`acceptances.${index}.value`)"
          v-model="acceptance.value"
        >
          <span v-html="acceptance.text"></span>
        </pcg-checkbox>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import ApiCompanies from '../api/companies'
import ApiDictionaryAcceptances from '../api/dictionary_acceptances'
import ApiInstances from '../api/instances'
import ApiOffers from '../api/offers'
import ApiStatistics from '../api/statistics'
import ApiUsers from '../api/users'
import ApiWebpages from '../api/webpages'
import CompanyPopupMixin from '../mixins/company_popup'
import CvPopupMixin from '../mixins/cv_popup'
import WindowResizeMixin from '../mixins/window_resize'

export default {
  name: 'login',
  mixins: [WindowResizeMixin, CompanyPopupMixin, CvPopupMixin],
  data: function() {
    return {
      offersCount: 0,
      offersTrend: 0,
      companiesCount: 0,
      companiesTrend: 0,
      studentsAndGraduatesCount: 0,
      studentsTrend: 0,
      cvAcceptances: [],
      companyAcceptances: [],
      currentAcceptances: [],
      errors: [],
      instance: {
        companiesText: null,
        footerLogo: null,
        footerLogoNegative: null,
        footerBottomText: null,
        loginFooter: null,
        loginLogo: null,
        loginLogoNegative: null,
        footerUpperText: null,
        mainText: null,
        menuLogo: null,
        menuLogoNegative: null,
        showMainPageInfo: null
      },
      offers: [],
      offersLoaded: false,
      selectedOfferType: 'all',
      companies: [],
      companiesLoaded: false,
      news: [],
      newsLoaded: false
    }
  },
  created() {
    this.loginCallback()
    this.getBasicInfo()
    this.getOffers()
    this.getCompanies()
    this.getNews()
    ApiInstances.getInstance().then((response) => {
      this.instance = response.data.data.attributes
    })
  },
  watch: {
    windowWidth(newValue) {
      this.setBackgroundImageStyle()
    },
    contrast(newValue) {
      newValue ? this.clearBackgroundImage() : this.setBackgroundImage()
    }
  },
  computed: {
    ...mapGetters('page', ['contrast']),
    ...mapGetters('auth', ['currentRole', 'roles']),
    ...mapGetters('environment', ['env']),
    cvAcceptancesToSend() {
      return this.currentAcceptances.cv.map((acceptance) => ({
        dictionaryAcceptanceWordId: acceptance.dictionaryAcceptanceWordId,
        value: acceptance.value,
        required: acceptance.required
      }))
    },
    companyAcceptancesToSend() {
      return this.currentAcceptances.company.map((acceptance) => ({
        dictionaryAcceptanceWordId: acceptance.dictionaryAcceptanceWordId,
        value: acceptance.value,
        required: acceptance.required
      }))
    }
  },
  methods: {
    getNews() {
      this.newsLoaded = false
      ApiWebpages.getDashboardNews()
        .then((response) => {
          this.news = response.data.data.map(({ attributes, id }) => {
            return {
              id,
              ...attributes
            }
          })
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.newsLoaded = true
        })
    },
    getOffers() {
      const filters = {}
      if (this.selectedOfferType !== 'all') {
        filters.offer_type = this.selectedOfferType
      }
      this.offersLoaded = false
      ApiOffers.getDashboardOffers({ filters })
        .then((response) => {
          this.offers = response.data.data.map(({ attributes, id }) => {
            return {
              id,
              ...attributes
            }
          })
        })
        .catch((error) => {
          console.error(error)
          this.$toastr.e(this.$t('components.offers.abk_offers_list.offers_download_error'))
        })
        .finally(() => {
          this.offersLoaded = true
        })
    },
    getCompanies() {
      this.companiesLoaded = false
      ApiCompanies.getDashboardCompanies({ status: ['saved'] })
        .then((response) => {
          this.companies = response.data.data.map(({ attributes, id }) => {
            return {
              id,
              ...attributes
            }
          })
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.companiesLoaded = true
        })
    },
    selectOfferType(type) {
      this.selectedOfferType = type
      this.getOffers()
    },
    formatDate(date, format) {
      if (date) {
        const newDate = moment(date)
        return newDate.isValid() ? newDate.format(format) : null
      }
      return null
    },
    getDuration(item) {
      if (!item.publicationDate) return ''
      const start = moment(item.publicationDate).startOf('day')
      const now = moment().startOf('day')
      const diffDays = Math.max(0, now.diff(start, 'days'))

      if (diffDays === 0) return this.$t('views.new_login.added_today')
      if (diffDays === 1) return this.$t('views.new_login.added_yesterday')
      return this.$t('views.new_login.added_days_ago', { count: diffDays })
    },
    logout() {
      const role = this.currentRole
      this.$store
        .dispatch('auth/signOut')
        .then((response) => {
          // eslint-disable-next-line eqeqeq
          if (this.env.idpType == 'CAS') {
            // eslint-disable-next-line no-constant-condition
            if (role === 'graduate' || role === 'employer' || role === 'recruiter') {
              window.location = '/'
            } else {
              const idToken = response.data.idToken
              // window.location = `${process.env.ABK_OP_END_SESSION_ENDPOINT}?id_token_hint=${idToken}`
              window.location = `${this.env.abkOpEndSessionEndpoint}?id_token_hint=${idToken}`
            }
          } else {
            const idToken = response.data.idToken
            // window.location = `${process.env.ABK_OP_END_SESSION_ENDPOINT}?id_token_hint=${idToken}`
            window.location = `${this.env.abkOpEndSsessionEndpoint}?id_token_hint=${idToken}`
          }
        })
        .catch((error) => {
          // console.log(error.response.data.message)
          this.$toastr.e(error.response.data.message)
        })
    },
    getError(field) {
      if (this.errorExist(field)) {
        return this.errors[field].join(', ')
      }
      return ''
    },
    errorExist(field) {
      return !!(this.errors && this.errors[field])
    },
    async checkCurrentUserAcceptances() {
      let valid = false
      try {
        const response = await ApiUsers.checkAcceptances()
        valid = response.data.cvAcceptance && response.data.companyAcceptance
        if (!valid) {
          const userRoles = this.$store.getters['auth/roles']
          if (userRoles.includes('student') || userRoles.includes('graduate')) {
            response.data.currentCvAcceptances.forEach((acc) => {
              const idx = this.cvAcceptances.findIndex(
                (acceptance) =>
                  parseInt(acceptance.dictionaryAcceptanceWordId) === parseInt(acc.dictionaryAcceptanceWordId)
              )
              if (idx >= 0) {
                this.cvAcceptances[idx].value = acc.value
                this.cvAcceptances[idx].type = 'cv'
              }
            })
          }
          if (userRoles.includes('employer') || userRoles.includes('recruiter')) {
            response.data.currentCompanyAcceptances.forEach((acc) => {
              const idx = this.companyAcceptances.findIndex(
                (acceptance) =>
                  parseInt(acceptance.dictionaryAcceptanceWordId) === parseInt(acc.dictionaryAcceptanceWordId)
              )
              if (idx >= 0) {
                this.companyAcceptances[idx].value = acc.value
                this.companyAcceptances[idx].type = 'company'
              }
            })
          }
        }
        return valid
      } catch (err) {
        return false
      }
    },
    submitAcceptances(e) {
      e.preventDefault()
      ApiUsers.fillAcceptances({
        user: { cvAcceptances: this.cvAcceptancesToSend, companyAcceptances: this.companyAcceptancesToSend }
      })
        .then((response) => {
          const userRole = this.$store.getters['auth/currentRole']
          if (['student', 'graduate'].includes(userRole)) {
            this.$router.push({ name: 'student_dashboard_path' })
          } else if (userRole === 'employer' || userRole === 'recruiter') {
            this.$router.push({ name: 'employer_dashboard_path' })
          } else if (userRole === 'career_office') {
            this.$router.push({ name: 'career_office_dashboard_path' })
          } else {
            this.$router.push({ name: 'root_path' })
          }
          this.checkCompany()
          this.checkCv()
        })
        .catch((error) => {
          this.errors = error.response.data
          this.$toastr.e(this.$t('general.fields_not_filled'))
        })
    },
    getBasicInfo() {
      ApiStatistics.getBasicInfo().then((response) => {
        const {
          acceptedCompaniesTotalCount,
          acceptedOffersTotalCount,
          studentsOrGraduatesTotalCount,
          accepted_companies_trend,
          accepted_offers_trend,
          students_or_graduates_trend
        } = response.data
        this.offersCount = acceptedOffersTotalCount
        this.offersTrend = accepted_offers_trend
        this.companiesCount = acceptedCompaniesTotalCount
        this.companiesTrend = accepted_companies_trend
        this.studentsAndGraduatesCount = studentsOrGraduatesTotalCount
        this.studentsTrend = students_or_graduates_trend
      })
    },
    login() {
      window.location = '/users/auth/openid_connect'
    },
    loginCallback() {
      if (this.$route.query.code) {
        ApiDictionaryAcceptances.getDictionaryAcceptanceAllWords()
          .then((res) => {
            this.cvAcceptances = []
            this.companyAcceptances = []
            res.data.data.forEach((dw) => {
              if (dw.attributes.registration) {
                const a = {
                  key: dw.attributes.type,
                  dictionaryAcceptanceWordId: dw.id,
                  required: dw.attributes.registrationRequired || dw.attributes.status === 'required',
                  text: dw.attributes.value,
                  value: false,
                  name: dw.attributes.name,
                  wordType: dw.attributes.wordType
                }
                const b = {
                  key: dw.attributes.type,
                  dictionaryAcceptanceWordId: dw.id,
                  required: dw.attributes.registrationRequired || dw.attributes.status === 'required',
                  text: dw.attributes.value,
                  value: false,
                  name: dw.attributes.name,
                  wordType: dw.attributes.wordType
                }
                this.cvAcceptances.push(a)
                this.companyAcceptances.push(b)
              }
            })
            let sortByName = []
            sortByName.push(
              ...this.cvAcceptances.sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
              )
            )
            this.cvAcceptances = sortByName
            sortByName = []
            sortByName.push(
              ...this.companyAcceptances.sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
              )
            )
            this.companyAcceptances = sortByName
            this.setLogin()
          })
          .catch((err) => {
            this.$toastr.e(err)
            console.log(err)
          })
      } else {
        this.hideAppLoader()
      }
    },
    setLogin() {
      const query = { ...this.$route.query }
      this.$router.replace({ query: {} })
      this.$store
        .dispatch('auth/signIn', query)
        .then((response) => {
          this.checkCurrentUserAcceptances().then((result) => {
            if (result) {
              const redirectTo = this.$store.getters['auth/getRedirectTo']
              if (!Array.isArray(this.$store.getters['auth/roles']) || !this.$store.getters['auth/roles'].length) {
                this.$router.push({ name: 'role_choose_path' })
              } else if (redirectTo && redirectTo.name) {
                this.$store.dispatch('auth/clearRedirectTo')
                this.$router.push(redirectTo)
                this.checkCompany()
                this.checkCv()
              } else {
                const userRole = this.$store.getters['auth/currentRole']
                if (['student', 'graduate'].includes(userRole)) {
                  this.$router.push({ name: 'student_dashboard_path' })
                } else if (userRole === 'employer' || userRole === 'recruiter') {
                  this.$router.push({ name: 'employer_dashboard_path' })
                } else if (userRole === 'career_office') {
                  this.$router.push({ name: 'career_office_dashboard_path' })
                } else {
                  this.$router.push({ name: 'root_path' })
                }
                this.checkCompany()
                this.checkCv()
              }
            } else {
              let result = []
              const userRoles = this.$store.getters['auth/roles']
              if (userRoles.includes('employer') || userRoles.includes('recruiter')) {
                result.push(
                  this.companyAcceptances.filter(
                    (a) => !a.value && (a.key === 'employer' || a.key === 'cooperation' || a.key === 'survey')
                  )
                )
              }
              if (userRoles.includes('student') || userRoles.includes('graduate')) {
                result.push(this.cvAcceptances.filter((a) => !a.value && (a.key === 'cv' || a.key === 'survey')))
              }
              result = result.flat()
              this.currentAcceptances = result
              const sortByName = []
              sortByName.push(
                ...this.currentAcceptances.sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
                )
              )
              this.currentAcceptances = sortByName
              this.currentAcceptances = {
                cv: this.currentAcceptances.filter((acc) => acc.type === 'cv'),
                company: this.currentAcceptances.filter((acc) => acc.type === 'company')
              }
              this.$refs.acceptancesModal.show()
            }
          })
        })
        .catch((error) => {
          console.log(error.response.data)
          this.$toastr.e(error.response.data)
        })
        .finally(() => {
          this.hideAppLoader()
        })
    },
    setBackgroundImageStyle() {
      this.$store.dispatch('pageCover/setBackgroundImageStyle', {
        height: '100% !important'
      })
    },
    setBackgroundImage() {
      if (!this.contrast) {
        this.$store.dispatch('pageCover/setBackgroundImage', require('../assets/images/blank.png'))
      }
    },
    setBackgroundImageClasses() {
      this.$store.dispatch('pageCover/setBackgroundImageClasses', ['no-gradient'])
    },
    setSidebarMenuClasses() {
      this.$store.dispatch('sidebarMenu/setSidebarMenuClasses', ['no-box-shadow'])
    },
    clearBackgroundImage() {
      this.$store.dispatch('pageCover/clearBackgroundImage')
    },
    hideAppLoader() {
      setTimeout(() => {
        const loader = document.getElementById('app-loader')
        if (loader) loader.style.display = 'none'
      }, 2000)
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.setBackgroundImage()
      vm.setBackgroundImageStyle()
      vm.setBackgroundImageClasses()
      vm.setSidebarMenuClasses()
    })
  }
}
</script>

<style lang="scss" scoped>
@import '../assets/stylesheets/vars';

.home {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding-bottom: 5rem;
}

/* Hero Section */
.hero {
  position: relative;
  background: linear-gradient(0deg, rgba(15, 23, 42, 0.65), rgba(15, 23, 42, 0.65)),
    url('../assets/images/new_hero.png');
  background-size: cover;
  background-position: center;
  height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  padding: 0;
  margin-bottom: 190px;
}

.hero-title {
  margin-bottom: 24px;
  max-width: 900px;
  font-weight: 900;
  font-size: 60px;
  line-height: 60px;
  letter-spacing: -1.5px;
  text-align: center;
  vertical-align: middle;

  .highlight {
    color: $main-active-color;
  }
}

.hero-subtitle {
  max-width: 700px;
  font-weight: 500;
  font-size: 18px;
  line-height: 29.25px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  color: #f1f5f9;
}

/* Stats Cards */
.stats-container {
  position: absolute;
  bottom: -60px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2rem;
  width: 90%;
  max-width: 1200px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 32px 2rem;
  flex: 1;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.stat-label {
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 1.4px;
  text-align: center;
  vertical-align: middle;
  text-transform: uppercase;
  margin-bottom: 8px;
  color: #64748b;
}

.stat-value-group {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-weight: 900;
  font-size: 36px;
  line-height: 36px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  margin-bottom: 4px;
  color: #0f172a;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 900;
  color: #222;
}

.stat-trend {
  font-size: 0.875rem;
  font-weight: 600;

  &.trend-up {
    color: #28a745;
  }
}

@media (min-width: 992px) {
  .main-row {
    margin: 0 -24px;
    justify-content: space-between;
  }

  .main-grid-col {
    padding: 0 24px;
    flex: 0 0 calc(66.666667% - 16px);
    max-width: calc(66.666667% - 16px);
  }

  .sidebar-col {
    padding: 0 24px;
    flex: 0 0 calc(33.333333% - 32px);
    max-width: calc(33.333333% - 32px);
  }
}

.stat-meta {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  color: #94a3b8;
}

/* Main Content Layout */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.section-title {
  font-weight: 900;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #0f172a;
}

.view-all {
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  color: $main-active-color;

  &:hover {
    text-decoration: underline;
  }
}

.filter-group {
  display: flex;
  gap: 1rem;
  margin-bottom: 32px;
  overflow-x: auto;
}

.filter-btn {
  padding: 8px 16px;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #fff;
  white-space: nowrap;
  transition: all 0.2s;
  color: #475569;

  &:hover {
    border-color: #00b4eb;
    background: #00b4eb;
    color: #ffffff;
  }

  &.active {
    background: #00b4eb;
    border-color: #00b4eb;
    color: #ffffff;
  }
}

/* Job Grid */
.job-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 24px;
}

.job-card {
  background: white;
  border-radius: 24px;
  padding: 25px;
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #d1e9ff;
  }
}

.job-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 24px;
}

.company-logo-placeholder {
  width: 300px;
  height: 90px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f1f5f9;

  img {
    object-fit: contain;
    max-height: 100%;
    max-width: 100%;
  }

  .logo-text {
    font-size: 0.6rem;
    font-weight: 700;
    color: #999;
  }
}

.job-type-tag {
  padding: 4px 8px;
  border-radius: 8px;
  font-weight: 900;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0px;
  vertical-align: middle;
  text-transform: uppercase;

  &.type-practice {
    background: #faf5ff;
    color: #9333ea;
  }

  &.type-training {
    background: #eff6ff;
    color: #2563eb;
  }

  &.type-job {
    background: #ecfdf5;
    color: #059669;
  }
}

.job-title {
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  vertical-align: middle;
  margin-bottom: 4px;
  color: #0f172a;
}

.job-company {
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0px;
  vertical-align: middle;
  margin-bottom: 16px;
  color: #64748b;
}

.tech-tags {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 16px;
}

.tech-tag {
  font-weight: 700;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #475569;
  margin-bottom: 16px;
  background-color: #f1f5f9;
  padding: 4px 8px;
  border-radius: 8px;
}

.job-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f8fafc;
}

.job-salary {
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #0f172a;
}

.job-date {
  font-weight: 500;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #94a3b8;
}

/* Sidebar Widgets */
.newsletter-card {
  background: #00acee;
  background: linear-gradient(135deg, #4fc3f7, #007bff);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  margin-bottom: 2rem;
}

.widget-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid #eee;
  margin-bottom: 2rem;
}

.widget-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
}

.widget-title-small {
  font-weight: 700;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #0f172a;
  margin-bottom: 24px;
}

.widget-text {
  font-size: 0.95rem;
  opacity: 0.9;
  margin-bottom: 1.5rem;
  line-height: 1.4;
}

.newsletter-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.newsletter-input {
  padding: 0.8rem 1rem;
  border-radius: 8px;
  border: none;
  font-size: 0.95rem;

  &::placeholder {
    color: #aaa;
  }
}

.btn-submit {
  padding: 0.8rem;
  border-radius: 8px;
  border: none;
  background: white;
  color: #007bff;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
}

.employer-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 40px;
}

.employer-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background-color: #fff;
  border-radius: 16px;
}

.employer-logo-sm {
  width: 48px;
  height: 48px;
  background: #fff;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.employer-info {
  display: flex;
  flex-direction: column;
}

.employer-name {
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #1e293b;
}

.employer-offers {
  font-weight: 400;
  font-size: 11px;
  line-height: 16.5px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #64748b;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.news-item {
  position: relative;
  padding-left: 1.5rem;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 6px;
    width: 8px;
    height: 8px;
    background: #e2e8f0;
    border-radius: 50%;
  }

  &:first-child:before {
    background: #00b4eb;
  }
}

.news-date {
  display: block;
  font-weight: 700;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0px;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
}

.news-item:first-child .news-date {
  color: #00b4eb;
}

.news-item-title {
  font-weight: 700;
  font-size: 14px;
  line-height: 19.25px;
  letter-spacing: 0px;
  vertical-align: middle;
  margin-bottom: 4px;
  color: #1e293b;
}

.news-item-text {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Responsive Adjustments */
@media (max-width: 991px) {
  .hero {
    height: 550px;
    margin-bottom: 220px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .stats-container {
    flex-direction: column;
    gap: 1rem;
    bottom: -180px;
  }

  .stat-card {
    padding: 1rem;
  }

  .stat-value {
    font-size: 2rem;
  }
}

@media (max-width: 576px) {
  .hero-title {
    font-size: 2rem;
  }

  .job-grid {
    grid-template-columns: 1fr;
  }
}

/* Re-integrating modal styles from original */
.missing-approvals {
  margin-bottom: 2rem;
  font-size: $font-size-m;
  font-weight: 400;
  color: $pcg-gray;
}

.missing-approvals-text {
  margin-bottom: 2rem;
  font-weight: 400;
  color: $pcg-gray;
}

.contrast {
  .home {
    background: transparent;
  }

  .highlight,
  .section-title,
  .view-all,
  .job-title {
    color: #00fa53;
  }

  .filter-btn {
    background-color: $pcg-black;
    color: #00fa53;
    border: solid 0.14rem #00fa53;
  }

  .filter-btn:hover,
  .filter-btn.active,
  .tech-tag {
    background-color: #00fa53;
    color: $pcg-black;
  }

  .stat-card {
    background-color: $pcg-black;

    .stat-value-group .stat-value {
      color: #00fa53;
    }

    .stat-label,
    .stat-meta {
      color: $pcg-gray;
    }
  }

  .job-card {
    background-color: $pcg-black;
    border-color: #4f4f4f;

    .company-logo-placeholder {
      border-color: #4f4f4f;
    }

    .job-company,
    .job-salary,
    .job-date {
      color: $pcg-gray;
    }
  }

  .newsletter-card {
    background: $pcg-black;

    .btn-submit {
      background-color: $pcg-black;
      color: #00fa53;
      border: solid 0.14rem #00fa53;
    }
  }

  .widget-title-small,
  .widget-title {
    color: #00fa53;
  }

  .widget-text {
    color: $pcg-gray;
  }

  .newsletter-input {
    color: $pcg-white;
    background-color: $pcg-black;
    border: 1px solid #f7f8fa !important;
  }

  .employer-item {
    background-color: $pcg-black;

    .employer-logo-sm {
      background: transparent;
      border: 1px solid #4f4f4f !important;
    }

    .employer-offers {
      color: $pcg-gray;
    }
  }

  .news-item:before {
    background: $pcg-gray;
  }
  .news-item:first-child:before {
    background: #00fa53;
  }

  .news-item {
    .news-date {
      color: #00fa53;
    }

    .news-item-title {
      color: #00fa53;
    }

    .news-item-text {
      color: $pcg-gray;
    }
  }
}
</style>
